import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPatch, apiPost } from 'api'
import AuthContext from 'context/auth-context'

const getUsers = async (token, params = {}) => {
  const url = 'admin/users'
  const data = await apiGet({ url, token, params })
  return data
}

const getUser = async (token, userId) => {
  const url = `admin/users/${userId}`
  const data = await apiGet({ url, token })
  return data
}

const getAiActivitySummary = async (token, userId, params = {}) => {
  const url = `admin/users/${userId}/ai_activity_summary`
  const data = await apiGet({ url, token, params })
  return data
}

const blockUser = async (token, userId) => {
  const url = `admin/users/${userId}/block`
  const data = await apiPost({ url, token, objectName: 'user', params: {} })
  return data
}

const unblockUser = async (token, userId) => {
  const url = `admin/users/${userId}/unblock`
  const data = await apiPost({ url, token, objectName: 'user', params: {} })
  return data
}

const getUserUsage = async (token, userId, endpoint, params) => {
  const url = `admin/users/${userId}/${endpoint}`
  const data = await apiGet({ url, token, params })
  return data
}

const updateUser = async (token, userId, params) => {
  const url = `admin/users/${userId}`
  const data = await apiPatch({ url, token, objectName: 'user', params })
  return data
}

const sendPasswordReset = async (token, userId) => {
  const url = `admin/users/${userId}/password_reset`
  const data = await apiPost({ url, token, objectName: 'user', params: {} })
  return data
}

// Cross-municipality users table. `params` carries search, filters, ordering and
// pagination straight through to the API, so the query key has to include them.
export const useAdminUsers = ({ params = {}, options } = {}) => {
  const token = useContext(AuthContext)
  return useQuery(
    ['admin-users', JSON.stringify(params)],
    () => getUsers(token, params),
    { keepPreviousData: true, ...options }
  )
}

export const useAdminUser = ({ userId, options } = {}) => {
  const token = useContext(AuthContext)
  return useQuery(
    ['admin-user', userId],
    () => getUser(token, userId),
    { enabled: !!userId, ...options }
  )
}

export const useAdminUserUsage = ({ userId, endpoint, params, options } = {}) => {
  const token = useContext(AuthContext)
  return useQuery(
    ['admin-user-usage', userId, endpoint, params],
    () => getUserUsage(token, userId, endpoint, params),
    { enabled: !!userId, ...options }
  )
}

export const useUpdateAdminUser = ({ userId, options } = {}) => {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (params) => updateUser(token, userId, params),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-user', userId])
        if (options?.onSuccess) options.onSuccess(data, variables)
      }
    }
  )
}

export const useBlockAdminUser = ({ userId, options } = {}) => {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    () => blockUser(token, userId),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-user', userId])
        if (options?.onSuccess) options.onSuccess(data, variables)
      }
    }
  )
}

export const useUnblockAdminUser = ({ userId, options } = {}) => {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    () => unblockUser(token, userId),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-user', userId])
        if (options?.onSuccess) options.onSuccess(data, variables)
      }
    }
  )
}

export const useSendPasswordReset = ({ userId, options } = {}) => {
  const token = useContext(AuthContext)

  return useMutation(
    () => sendPasswordReset(token, userId),
    options
  )
}

// The AI activity summary is written by a model on the server and cached there
// against the user's latest event, so this only needs to ask once per page
// visit — window focus should not spend another round trip on it.
export const useAdminUserAiSummary = ({ userId, options } = {}) => {
  const token = useContext(AuthContext)
  return useQuery(
    ['admin-user-ai-summary', userId],
    () => getAiActivitySummary(token, userId),
    { enabled: !!userId, refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, ...options }
  )
}

// Forces a fresh generation past the server-side cache, and seeds the result
// into the query above so the card re-renders without a second request.
export const useRefreshAdminUserAiSummary = ({ userId, options } = {}) => {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    () => getAiActivitySummary(token, userId, { refresh: true }),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.setQueryData(['admin-user-ai-summary', userId], data)
        if (options?.onSuccess) options.onSuccess(data, variables)
      }
    }
  )
}
